<script setup lang="ts">
import KartNavigation from "../../../components/website/KartNavigation.vue";
import ImageHeader from "../../../components/website/ImageHeader.vue";
import Who from "../../../components/website/LayoutForWho.vue";
import { useTranslate } from "addeus-common-library/stores/translate";
import { useOwner } from "../../../stores/website/owner";
import PackCard from "../../../components/website/PackCard.vue";
import TranslateNamespace from "addeus-common-library/components/base/translation/TranslateNamespace.vue";
import { useSeoMeta } from "../../../composables/useSeoMeta";

const { setTranslateNamespace } = useTranslate();
setTranslateNamespace(".kart.afterWork");

const ownerManager = useOwner();
function removePrefixPhoneNumber(phoneNumber: string) {
    return phoneNumber.replace(/^\+[0-9]+\s*/, "0");
}

useSeoMeta({
    title: "Afterwork karting électrique | Soirée team building entre collègues",
    description:
        "Afterwork karting électrique entre collègues : piste privatisée, Grand Prix en 3 runs et consommation incluse. Soirée entreprise originale et team building informel.",
    path: "/kart/afterWork",
    breadcrumbs: [
        { name: "Accueil", path: "/" },
        { name: "Faire du kart", path: "/kart" },
        { name: "Afterwork", path: "/kart/afterWork" },
    ],
    schemaOrg: [
        {
            "@context": "https://schema.org",
            "@type": "Event",
            name: "Afterwork karting entre collègues",
            description:
                "Afterworks karting avec piste privatisée, Grand Prix et consommation incluse.",
            url: "https://speed2max.com/kart/afterWork",
            eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
            organizer: {
                "@type": "Organization",
                name: "Speed2Max",
                url: "https://speed2max.com",
            },
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
                {
                    "@type": "Question",
                    name: "Que comprend la formule Afterwork karting ?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "La formule Afterwork comprend 3 runs (qualification, demi-finale, finale), un Grand Prix sur piste privatisée et une consommation incluse.",
                    },
                },
                {
                    "@type": "Question",
                    name: "Combien de personnes pour un afterwork karting ?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "L'afterwork karting est idéal pour les groupes de collègues. Contactez-nous pour connaître les conditions de privatisation selon votre piste.",
                    },
                },
            ],
        },
    ],
});
</script>

<template>
    <VFlex flex-direction="column" class="has-background-dark">
        <KartNavigation current="afterWork"></KartNavigation>
        <ImageHeader secondary>
            <template #image>
                <img
                    src="/images/kart/faireDuKart/FDK-afterwork-header.jpg"
                    alt="Afterwork karting entre collègues Speed2Max" />
            </template>
            <template #title>
                <span> <Translate path=".title"></Translate></span>
            </template>
            <template #subtitle>
                <span class="has-text-white">
                    <Translate path=".subtitle"></Translate>
                    <span class="has-text-primary">
                        <Translate path=".subtitle2"></Translate>
                    </span>
                </span>
            </template>
        </ImageHeader>
        <Who>
            <template #title>
                <Translate path=".title"></Translate>
            </template>
            <template #content>
                <Translate path=".content"></Translate>
            </template>
            <template #moreInfos>
                <Translate path=".moreInfos"></Translate>
            </template>
            <template #icon>
                <img
                    src="/images/kart/afterwork-icon.png"
                    alt="Icône afterwork karting" />
            </template>
            <template #titleP1>
                <Translate path=".titleP1"></Translate>
            </template>
            <template #titleP2>
                <Translate path=".titleP2"></Translate>
            </template>
        </Who>
        <div style="display: grid">
            <PackCard
                head-image="/images/kart/afterwork-pack.png"
                :price="ownerManager.owner?.website?.priceAfterWorkPack"
                :precision="1">
                <template #content>
                    <TranslateNamespace path=".pack">
                        <VFlex
                            flex-direction="column"
                            class="is-flex is-align-self-stretch py-3 is-size-content"
                            align-items="start">
                            <p class="has-text-white is-uppercase my-2 is-weight-600">
                                <span class="has-text-primary">•&nbsp; 3</span>
                                <Translate path=".feature1"></Translate>
                            </p>
                            <p
                                class="has-text-white is-uppercase my-2 mx-4 is-weight-600">
                                <span class="has-text-primary">•&nbsp; RUN 1 </span>
                                <Translate path=".feature11"></Translate>
                            </p>
                            <p
                                class="has-text-white is-uppercase my-2 mx-4 is-weight-600">
                                <span class="has-text-primary">•&nbsp; RUN 2 </span>
                                <Translate path=".feature12"></Translate>
                            </p>
                            <p
                                class="has-text-white is-uppercase my-2 mx-4 is-weight-600">
                                <span class="has-text-primary">•&nbsp; RUN 3</span>
                                <Translate path=".feature13"></Translate>
                            </p>
                            <p class="has-text-white is-uppercase my-2 is-weight-600">
                                <span class="has-text-primary">•&nbsp;</span>
                                <Translate path=".feature2"></Translate>
                            </p>
                            <p class="has-text-white is-uppercase my-2 is-weight-600">
                                <span class="has-text-primary">•&nbsp;</span>
                                <Translate path=".feature3"></Translate>
                            </p>
                            <p class="has-text-white is-uppercase my-2 is-weight-600">
                                <span class="has-text-primary">•&nbsp;</span>
                                <Translate path=".feature4"></Translate>
                            </p>
                        </VFlex>
                    </TranslateNamespace>
                </template>
            </PackCard>
        </div>
        <VFlex class="px-6 py-5 mb-5" flex-direction="column">
            <VFlex v-if="ownerManager.ownerID === null" :flex-direction="'column'">
                <p class="is-size-6 mb-4">
                    <Translate path=".infoChooseOwner"></Translate>
                </p>
                <ButtonPrimary
                    color="primary"
                    class="is-align-self-flex-start mt-none is-mohol is-size-6"
                    style="font-family: mohol !important"
                    @click="$router.push('/owners')">
                    <!-- <VIcon icon="/images/kart/book-button-1.png"></VIcon> -->
                    <span class="is-uppercase">
                        <Translate path=".selectOwner"></Translate>
                    </span>
                </ButtonPrimary>
            </VFlex>
            <Banner class="m-5 has-gradient-background-dark-ltr w-100 is-weight-600">
                <Translate path=".banner"></Translate><br />
                <a
                    v-if="ownerManager.owner"
                    class="is-weight-900"
                    :href="`tel:${ownerManager.owner?.phoneNumber}`"
                    >{{ removePrefixPhoneNumber(ownerManager.owner?.phoneNumber) }}</a
                >
            </Banner>
        </VFlex>

        <Footer></Footer>
    </VFlex>
</template>

<style scoped lang="scss">
.title-afterwork {
    max-height: 100px;
}

@media (max-width: 700px) {
    .title-afterwork {
        max-height: 65px;
    }
}

.w-100 {
    width: 100%;
}

.pack-block {
    position: relative;
    border-radius: 5px;

    .is-size-content {
        font-size: 1.5rem;
    }

    .top-right-img {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 1;
        max-height: 24%;
    }

    @media (max-width: 700px) {
        .top-right-img {
            max-height: 70px;
        }

        .is-size-content {
            font-size: 1.2rem;
        }
    }
}

.banner {
    a {
        &:hover {
            color: white;
        }
    }
}
</style>
